import { Injectable, inject } from '@angular/core';
import { HubConnection, HubConnectionBuilder, HubConnectionState } from '@microsoft/signalr';
import { Subject, Observable, BehaviorSubject } from 'rxjs';
import { environment } from '../../environments/environment';
import { Router } from '@angular/router';
import {
  ScannerSessionInfo,
  ScannerSessionActivation,
  ScannerDeviceState,
  QrCodeScanResult
} from '../models/qr-scanner.models';
import { ScannerPairingService } from './scanner-pairing.service';

@Injectable({
  providedIn: 'root'
})
export class QrScannerSignalRService {
  private pairingService = inject(ScannerPairingService);

  private connection: HubConnection | null = null;
  private connectionState$ = new BehaviorSubject<HubConnectionState>(HubConnectionState.Disconnected);

  /**
   * Em modo dispositivo o cliente é um celular pareado, sem login: autentica
   * com o token de scanner e nunca é mandado para a tela de login.
   */
  private deviceMode = false;

  // Eventos como Observables
  private qrCodeScannedSubject = new Subject<QrCodeScanResult>();
  public onQrCodeScanned$: Observable<QrCodeScanResult> = this.qrCodeScannedSubject.asObservable();

  private mobileConnectedSubject = new Subject<ScannerSessionInfo>();
  public onMobileConnected$: Observable<ScannerSessionInfo> = this.mobileConnectedSubject.asObservable();

  private mobileDisconnectedSubject = new Subject<void>();
  public onMobileDisconnected$: Observable<void> = this.mobileDisconnectedSubject.asObservable();

  private qrCodeDataReceivedSubject = new Subject<{success: boolean, scannedAt: string}>();
  public onQrCodeDataReceived$: Observable<{success: boolean, scannedAt: string}> = this.qrCodeDataReceivedSubject.asObservable();

  private sessionDisconnectedSubject = new Subject<void>();
  public onSessionDisconnected$: Observable<void> = this.sessionDisconnectedSubject.asObservable();

  /** Um aparelho leu o QR e está aguardando confirmação no desktop. */
  private pairingAttemptSubject = new Subject<{deviceLabel: string, pending: number}>();
  public onPairingAttempt$: Observable<{deviceLabel: string, pending: number}> = this.pairingAttemptSubject.asObservable();

  /** A estação (desktop) caiu ou voltou — visto do lado do celular. */
  private stationOnlineSubject = new Subject<boolean>();
  public onStationOnline$: Observable<boolean> = this.stationOnlineSubject.asObservable();

  /** O desktop reconectou e precisa retomar a sessão que tinha. */
  private operatorReconnectedSubject = new Subject<void>();
  public onOperatorReconnected$: Observable<void> = this.operatorReconnectedSubject.asObservable();

  constructor(private router: Router) {
    this.initializeConnection();
  }

  /** Liga o modo dispositivo antes de conectar, na tela do celular. */
  useDeviceToken(): void {
    this.deviceMode = true;
  }

  private initializeConnection(): void {
    // Ancorado no fim de propósito. `replace('/api', '')` com texto troca a
    // PRIMEIRA ocorrência: em produção, onde a API é `https://api.flowtify.com.br/api`,
    // ele comia o `/api` de `https://api…` e devolvia `https:/.flowtify.com.br/api`.
    // O browser resolvia essa URL quebrada contra a origem do SPA, o negotiate
    // batia no nginx do frontend e voltava 405. Local e dev escapavam só porque
    // os hosts deles não começam com "api.".
    const baseUrl = environment.apiUrl.replace(/\/api\/?$/, '');
    const hubUrl = `${baseUrl}/hubs/qrCodeScanner`;

    this.connection = new HubConnectionBuilder()
      .withUrl(hubUrl, {
        accessTokenFactory: () => {
          if (this.deviceMode) {
            return this.pairingService.getToken() || '';
          }
          return localStorage.getItem('jwt') || localStorage.getItem('jwt-password') || '';
        }
      })
      .withAutomaticReconnect({
        nextRetryDelayInMilliseconds: (retryContext) => {
          if (retryContext.previousRetryCount < 3) {
            return 2000;
          } else if (retryContext.previousRetryCount < 10) {
            return 10000;
          } else {
            return 30000;
          }
        }
      })
      .build();

    this.connection.on('OnQrCodeScanned', (result: QrCodeScanResult) => {
      this.qrCodeScannedSubject.next(result);
    });

    this.connection.on('MobileConnected', (info: ScannerSessionInfo) => {
      this.mobileConnectedSubject.next(info);
    });

    this.connection.on('MobileDisconnected', () => {
      this.mobileDisconnectedSubject.next();
    });

    this.connection.on('QrCodeDataReceived', (data: {success: boolean, scannedAt: string}) => {
      this.qrCodeDataReceivedSubject.next(data);
    });

    this.connection.on('SessionDisconnected', () => {
      this.sessionDisconnectedSubject.next();
    });

    this.connection.on('PairingAttemptReceived', (data: {deviceLabel: string, pending: number}) => {
      this.pairingAttemptSubject.next(data);
    });

    // A estação saiu do ar mas o pareamento continua valendo — diferente de
    // SessionDisconnected, que desfaz o vínculo.
    this.connection.on('StationOffline', () => this.stationOnlineSubject.next(false));
    this.connection.on('StationOnline', () => this.stationOnlineSubject.next(true));

    this.connection.onreconnecting(() => {
      this.connectionState$.next(HubConnectionState.Reconnecting);
    });

    this.connection.onreconnected(async () => {
      this.connectionState$.next(HubConnectionState.Connected);

      // A conexão nova tem outro id no servidor, então as duas pontas precisam
      // se revincular — cada uma com o que já tem, sem refazer o pareamento.
      if (this.deviceMode) {
        try {
          await this.attachScannerDevice();
        } catch {
          this.sessionDisconnectedSubject.next();
        }
      } else {
        this.operatorReconnectedSubject.next();
      }
    });

    this.connection.onclose((error) => {
      this.connectionState$.next(HubConnectionState.Disconnected);

      if (error && !this.deviceMode) {
        this.router.navigate(['/login']);
      }
    });
  }

  async startConnection(): Promise<void> {
    if (!this.connection) {
      this.initializeConnection();
    }

    if (this.connection?.state === HubConnectionState.Disconnected) {
      try {
        await this.connection.start();
        this.connectionState$.next(HubConnectionState.Connected);
      } catch (error: any) {
        console.error('Erro ao conectar SignalR:', error);
        this.handleAuthError(error);
        throw error;
      }
    }
  }

  async stopConnection(): Promise<void> {
    if (this.connection && this.connection.state !== HubConnectionState.Disconnected) {
      try {
        await this.connection.stop();
        this.connectionState$.next(HubConnectionState.Disconnected);
      } catch (error) {
        console.error('Erro ao desconectar SignalR:', error);
      }
    }
  }

  getConnectionState(): Observable<HubConnectionState> {
    return this.connectionState$.asObservable();
  }

  isConnected(): boolean {
    return this.connection?.state === HubConnectionState.Connected;
  }

  // ------------------------------------------------------------------
  // Desktop autenticado
  // ------------------------------------------------------------------

  async activateScannerMode(): Promise<ScannerSessionActivation> {
    await this.startConnection();
    return this.invoke<ScannerSessionActivation>('ActivateScannerMode');
  }

  /** Emite um novo código de pareamento; o anterior deixa de valer. */
  async regeneratePairingCode(): Promise<ScannerSessionActivation> {
    await this.startConnection();
    return this.invoke<ScannerSessionActivation>('RegeneratePairingCode');
  }

  /**
   * Retoma uma sessão que continuou de pé enquanto o desktop esteve fora —
   * depois de um F5, de uma reconexão de rede ou de um deploy.
   */
  async reattachScannerSession(sessionId: string): Promise<ScannerSessionInfo> {
    await this.startConnection();
    return this.invoke<ScannerSessionInfo>('ReattachScannerSession', sessionId);
  }

  /**
   * Confirma o pareamento com os dígitos que apareceram no celular.
   * É este passo que decide qual aparelho vira o scanner: as demais
   * tentativas pendentes são descartadas.
   */
  async confirmScannerPairing(confirmationCode: string): Promise<ScannerSessionInfo> {
    await this.startConnection();
    return this.invoke<ScannerSessionInfo>('ConfirmScannerPairing', confirmationCode);
  }

  /** Desconecta o celular mantendo a sessão aberta. */
  async revokePairedDevice(): Promise<void> {
    if (!this.isConnected()) return;
    await this.invoke<void>('RevokePairedDevice');
  }

  async deactivateScannerMode(): Promise<void> {
    if (!this.isConnected()) return;
    await this.invoke<void>('DeactivateScannerMode');
  }

  // ------------------------------------------------------------------
  // Celular pareado
  // ------------------------------------------------------------------

  /**
   * Vincula esta conexão à sessão do token. Sem parâmetros de propósito:
   * sessão e dispositivo vêm do token, o celular não escolhe onde entrar.
   */
  async attachScannerDevice(): Promise<ScannerDeviceState> {
    this.useDeviceToken();
    await this.startConnection();
    return this.invoke<ScannerDeviceState>('AttachScannerDevice');
  }

  async leaveScannerSession(): Promise<void> {
    if (!this.isConnected()) return;
    await this.invoke<void>('LeaveScannerSession');
  }

  async sendQrCodeData(qrCodeData: string): Promise<void> {
    if (!this.connection || !this.isConnected()) {
      throw new Error('Conexão SignalR não estabelecida');
    }
    await this.invoke<void>('SendQrCodeData', qrCodeData);
  }

  // ------------------------------------------------------------------

  private async invoke<T>(method: string, ...args: any[]): Promise<T> {
    if (!this.connection || !this.isConnected()) {
      throw new Error('Conexão SignalR não estabelecida');
    }

    try {
      return await this.connection.invoke<T>(method, ...args);
    } catch (error: any) {
      console.error(`Erro ao chamar ${method}:`, error);
      this.handleAuthError(error);
      throw error;
    }
  }

  private handleAuthError(error: any): void {
    // Um celular pareado não tem login para onde voltar: perder o vínculo
    // significa ler o QR da estação de novo, não autenticar.
    if (this.deviceMode) {
      return;
    }

    if (error?.statusCode === 401) {
      this.router.navigate(['/login']);
    }
  }
}
